import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Multi, MultiLeg, MultiStatus, useMultis } from '../../queries/multis';
import { shiftDateIso, todayLocalIso } from '../../lib/date';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { ValueRadarBoard } from './ValueRadarBoard';
import { ChatMultis } from './ChatMultis';

const KIND_LABEL: Record<Multi['kind'], string> = {
  banker: 'Banker',
  strong: 'Strong chance',
  mid: 'Middle',
  value: 'Value',
  long: 'Long shot',
  value_pick: 'Value pick of the day',
};
const STATUS_VARIANT: Record<MultiStatus, 'success' | 'destructive' | 'outline' | 'default'> = { won: 'success', lost: 'destructive', void: 'outline', pending: 'default' };
const STATUS_LABEL: Record<MultiStatus, string> = { won: 'Won', lost: 'Lost', void: 'Void', pending: 'Pending' };

/** "73.4%" for a likely multi, "1 in 1,441" once a percentage would read as 0. */
export const chanceText = (chance: number | string): string => {
  const c = Number(chance);
  return c >= 0.1 ? `${(c * 100).toFixed(1)}%` : `1 in ${Math.round(1 / c).toLocaleString('en-AU')}`;
};
const money = (v: number | string | null) => (v == null ? '-' : `$${Number(v).toFixed(2)}`);
export const legOption = (leg: Pick<MultiLeg, 'finish_within' | 'field_size'>) =>
  leg.finish_within === 1 ? 'Win' : `Top ${leg.finish_within} of ${leg.field_size}`;

function MultiCard({ multi }: { multi: Multi }) {
  return (
    <Card aria-label={`${KIND_LABEL[multi.kind]}: ${multi.name}`}>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="primary">{KIND_LABEL[multi.kind]}</Badge>
          <CardTitle className="text-base">{multi.name}</CardTitle>
          <Badge variant={STATUS_VARIANT[multi.status]} className="ml-auto">
            {STATUS_LABEL[multi.status]}
          </Badge>
        </div>
        {multi.rationale && <p className="text-sm text-muted-foreground">{multi.rationale}</p>}
        <p className="text-sm">
          {multi.leg_count === 1 ? 'Single' : `${multi.leg_count} legs`} · <b>{chanceText(multi.chance)}</b> chance · est. <b>{money(multi.est_price)}</b>
          {multi.settled_price != null && Number(multi.settled_price) !== Number(multi.est_price) && <> (after voids {money(multi.settled_price)})</>}
        </p>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="py-1 pr-2">Race</th>
              <th className="py-1 pr-2">Horse</th>
              <th className="py-1 pr-2">Leg</th>
              <th className="py-1 pr-2">Chance</th>
              <th className="py-1">Result</th>
            </tr>
          </thead>
          <tbody>
            {multi.legs.map((leg) => (
              <tr key={leg.leg_no} className="border-t border-border">
                <td className="py-1.5 pr-2">{leg.track_name ?? leg.race_id} R{leg.race_number ?? ''}</td>
                <td className="py-1.5 pr-2">
                  #{leg.tab_number} {leg.horse_name}
                </td>
                <td className="py-1.5 pr-2">{legOption(leg)}</td>
                <td className="py-1.5 pr-2">{Math.round(Number(leg.chance) * 100)}%</td>
                <td className="py-1.5">
                  <Badge variant={STATUS_VARIANT[leg.status]}>
                    {STATUS_LABEL[leg.status]}
                    {leg.finish_position ? ` (${leg.finish_position})` : ''}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

/** The Multi Builder's selections for a day (five multis and the value pick of the day; n8n, daily 09:00), with how each leg went. */
export function MultisPage() {
  const [params, setParams] = useSearchParams();
  const date = params.get('date') || todayLocalIso();
  const { data: multis = [], isLoading, error } = useMultis(date);
  const go = (d: string) => setParams(d ? { date: d } : {});
  const stepClass =
    'inline-flex h-9 w-9 items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Multis"
        description="Five multis and the value pick of the day, built every racing day at 9:00am from our tips and Sportsbet's Win / Top N options."
      />
      <div className="flex items-center gap-2">
        <label htmlFor="multis-date" className="mr-1 text-sm font-medium">
          Date
        </label>
        <button type="button" onClick={() => go(shiftDateIso(date, -1))} aria-label="Previous day" className={stepClass}>
          <ChevronLeft className="h-4 w-4" />
        </button>
        <input
          id="multis-date"
          type="date"
          value={date}
          onChange={(e) => go(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        />
        {/* multis are built on the day, so there is never a tomorrow to step to */}
        <button type="button" onClick={() => go(shiftDateIso(date, 1))} disabled={date >= todayLocalIso()} aria-label="Next day" className={stepClass}>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <p className="text-xs text-muted-foreground">
        Chances are based on the market at build time. Prices are estimates (fair odds less a bookmaker margin), not bookmaker quotes.
      </p>
      {isLoading ? (
        <Spinner />
      ) : error ? (
        <p className="text-sm text-destructive">Could not load the multis.</p>
      ) : multis.length === 0 ? (
        <p className="text-sm text-muted-foreground">No multis were built for this day.</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {multis.map((m) => (
            <MultiCard key={m.id} multi={m} />
          ))}
        </div>
      )}
      <ValueRadarBoard date={date} />
      <ChatMultis date={date} />
    </div>
  );
}
